/**
 * Skills Analysis Component
 *
 * Displays detected role and skill analysis: relevant, missing, and
 * optionally redundant skills.
 *
 * Layout:
 *   Detected Role: Director of Product Engineering
 *   [check] Relevant: Agile, Scrum, CI/CD, Team Leadership
 *   [x]     Missing:  OKR, Stakeholder Management, P&L
 *   [~]     Redundant: jQuery (optional)
 */

import React from 'react';
import { CheckCircle2, XCircle, MinusCircle, Briefcase } from 'lucide-react';
import type { SkillsAnalysisProps } from '../types';

export const SkillsAnalysis: React.FC<SkillsAnalysisProps> = ({ skills }) => {
  const hasRelevant = skills.relevantSkills.length > 0;
  const hasMissing = skills.missingSkills.length > 0;
  const hasRedundant = skills.redundantSkills && skills.redundantSkills.length > 0;

  if (!hasRelevant && !hasMissing && !hasRedundant && !skills.detectedRole) {
    return null;
  }

  return (
    <div className="ats-report-skills">
      {/* Detected Role */}
      {skills.detectedRole && (
        <div className="ats-report-skills__role">
          <Briefcase className="ats-report-skills__role-icon" />
          <span className="ats-report-skills__role-label">Detected Role:</span>
          <span className="ats-report-skills__role-value">{skills.detectedRole}</span>
        </div>
      )}

      {/* Relevant Skills */}
      {hasRelevant && (
        <div className="ats-report-skills__group ats-report-skills__group--relevant">
          <div className="ats-report-skills__group-header">
            <CheckCircle2 className="ats-report-skills__group-icon ats-report-skills__group-icon--relevant" />
            <span className="ats-report-skills__group-label">Relevant Skills</span>
            <span className="ats-report-skills__group-count">
              {skills.relevantSkills.length}
            </span>
          </div>
          <div className="ats-report-skills__tags">
            {skills.relevantSkills.map((skill, i) => (
              <span
                key={i}
                className="ats-report-skills__tag ats-report-skills__tag--relevant"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Missing Skills */}
      {hasMissing && (
        <div className="ats-report-skills__group ats-report-skills__group--missing">
          <div className="ats-report-skills__group-header">
            <XCircle className="ats-report-skills__group-icon ats-report-skills__group-icon--missing" />
            <span className="ats-report-skills__group-label">Missing Skills</span>
            <span className="ats-report-skills__group-count">
              {skills.missingSkills.length}
            </span>
          </div>
          <div className="ats-report-skills__tags">
            {skills.missingSkills.map((skill, i) => (
              <span
                key={i}
                className="ats-report-skills__tag ats-report-skills__tag--missing"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Redundant Skills */}
      {hasRedundant && (
        <div className="ats-report-skills__group ats-report-skills__group--redundant">
          <div className="ats-report-skills__group-header">
            <MinusCircle className="ats-report-skills__group-icon ats-report-skills__group-icon--redundant" />
            <span className="ats-report-skills__group-label">Redundant Skills</span>
            <span className="ats-report-skills__group-count">
              {skills.redundantSkills!.length}
            </span>
          </div>
          <div className="ats-report-skills__tags">
            {skills.redundantSkills!.map((skill, i) => (
              <span
                key={i}
                className="ats-report-skills__tag ats-report-skills__tag--redundant"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

SkillsAnalysis.displayName = 'SkillsAnalysis';

export default SkillsAnalysis;
